import * as React from 'react';
import { FunctionComponent } from 'react';
import { Field, Form, Formik } from 'formik';
import * as Yup from 'yup';
import propz from 'propz';
import { Tournament } from 'Src/models/tournament';
import { GENDER, SPORT_GENDER_SERVER_TO_CLIENT_MAPPING } from 'Src/consts/common';
import { TOURNAMENT_SCHEDULE_MAPPING_CLIENT_TO_SERVER } from 'Src/consts/tournament';
import { AppUser } from 'Src/views/App/App';
import { getSelectOptionForTournamentEventAgesGroup } from 'Src/helpers/table/select';
import { DatePicker } from 'Src/components/DatePicker/DatePicker';
import { TimePicker } from 'Src/components/TimePicker/TimePicker';
import { getAllSchoolUnionTournamentTeams } from 'Src/helpers/service/adminSU/tournamentTeamsTable';
import { isMiniTeamTournament } from 'Src/helpers/tournament/tournament';
import { TournamentTeam } from 'Src/models/tournamentTeam';
import './TournamentEventsGroupsForm.scss';

interface Props {
  onCancel: () => void;
  onSubmit: (data: any) => void;
  tournament: Tournament;
  user?: AppUser;
  isSubmitting?: boolean;
}

const schema = Yup.object().shape({
  tournamentGroupId: Yup.string().required('Select a group'),
  startTime: Yup.date().required('Select a start date and time'),
  eventDuration: Yup.number()
    .typeError('Enter an event duration')
    .integer('Use whole minutes')
    .min(1, 'Duration must be at least 1 minute')
    .required('Enter an event duration'),
  breakBetweenEvents: Yup.number()
    .typeError('Enter the break between events')
    .integer('Use whole minutes')
    .min(0, 'Break cannot be negative')
    .required('Enter the break between events'),
  rounds: Yup.number().integer().min(1).max(10).required(),
  awardPlaces: Yup.number().integer().min(1).max(20).required()
});

const applyDatePart = (value: Date, date: Date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate(), value.getHours(), value.getMinutes(), 0, 0);

const applyTimePart = (value: Date, time: Date) =>
  new Date(value.getFullYear(), value.getMonth(), value.getDate(), time.getHours(), time.getMinutes(), 0, 0);

const PitchPicker: React.FC<{pitches:any[];value:string;onChange:(value:string)=>void}> = ({pitches,value,onChange}) => {
  const [open,setOpen]=React.useState(false);
  const ref=React.useRef<HTMLDivElement>();
  React.useEffect(()=>{const close=(event:MouseEvent)=>{if(ref.current&&!ref.current.contains(event.target as Node))setOpen(false);};document.addEventListener('mousedown',close);return()=>document.removeEventListener('mousedown',close);},[]);
  const selected=pitches.find(pitch=>String(pitch.id)===String(value));
  return <div className={`tournamentGroupEventsForm__pitchPicker ${open?'is-open':''}`} ref={ref}><button type="button" className="tournamentGroupEventsForm__pitchTrigger" aria-haspopup="listbox" aria-expanded={open} onClick={()=>setOpen(state=>!state)}><span>{selected?.name||'Not assigned'}</span><svg viewBox="0 0 24 24" aria-hidden="true"><path d="m7 10 5 5 5-5"/></svg></button>{open&&<div className="tournamentGroupEventsForm__pitchMenu" role="listbox"><button type="button" role="option" aria-selected={!value} className={!value?'is-selected':''} onClick={()=>{onChange('');setOpen(false);}}><span>Not assigned</span>{!value&&<b>✓</b>}</button>{pitches.map(pitch=><button type="button" role="option" aria-selected={String(value)===String(pitch.id)} className={String(value)===String(pitch.id)?'is-selected':''} key={pitch.id} onClick={()=>{onChange(pitch.id);setOpen(false);}}><span>{pitch.name}</span>{String(value)===String(pitch.id)&&<b>✓</b>}</button>)}</div>}</div>;
};

export const TournamentEventsGroupForm: FunctionComponent<Props> = ({ tournament, user, onCancel, onSubmit, isSubmitting=false }) => {
  const [teams,setTeams]=React.useState<TournamentTeam[]>([]);
  React.useEffect(()=>{
    let active=true;
    if(user&&tournament.id)getAllSchoolUnionTournamentTeams(user,tournament.id).then(items=>{if(active)setTeams(items);}).catch(()=>{if(active)setTeams([]);});
    return()=>{active=false;};
  },[user,tournament.id]);
  const groupStages=(tournament.tournamentLayout||[]).filter(stage=>stage.type==='GROUP');
  const groupStageIds=new Set(groupStages.map(stage=>String(stage.id||(stage as any)._id)));
  const stageOrder=new Map((tournament.tournamentLayout||[]).map((stage,index)=>[String(stage.id||(stage as any)._id),index]));
  const groups = [...(tournament.customGroups || [])].filter(group=>groupStageIds.has(String(group.stageId))).sort((left,right)=>{
    const stageDifference=(stageOrder.get(String(left.stageId))??Number.MAX_SAFE_INTEGER)-(stageOrder.get(String(right.stageId))??Number.MAX_SAFE_INTEGER);
    return stageDifference||String(left.name||'').localeCompare(String(right.name||''),undefined,{numeric:true,sensitivity:'base'});
  });
  const pitches = tournament.pitches || [];
  const gender =
    tournament.gender === GENDER.MIXED && !tournament.isMixedTeamsAvailable ? GENDER.MALE_ONLY : tournament.gender;
  const age = propz.get(tournament, ['ages', '0'], '');
  const ageOptions = getSelectOptionForTournamentEventAgesGroup(tournament, user);
  const ageOption: any = ageOptions.find((option: any) => String(option.value) === String(age));
  const ageLabel = ageOption ? ageOption.text || ageOption.label : `U${age}`;
  const genderLabel = SPORT_GENDER_SERVER_TO_CLIENT_MAPPING[gender] || gender;

  const initialValues = {
    gender,
    age,
    tournamentGroupId: groups.length ? groups[0].id : '',
    schedule: TOURNAMENT_SCHEDULE_MAPPING_CLIENT_TO_SERVER.ROUND_ROBIN,
    startTime: new Date(tournament.startTime),
    eventDuration: tournament.competitionStructure?.draft?.scheduleDefaults?.groupDuration || 20,
    breakBetweenEvents: tournament.competitionStructure?.draft?.scheduleDefaults?.groupBreak ?? 5,
    pitchId: '',
    rounds: groups[0]?.roundRobinRounds || 1,
    competitionOutcome: groups[0]?.competitionOutcome || 'KNOCKOUT',
    awardPlaces: groups[0]?.awardPlaces || 3
  };

  return (
    <div className="tournamentGroupEventsForm">
      <div className="tournamentGroupEventsForm__chips" aria-label="Event details">
        <span className="is-gender">{genderLabel}</span>
        <span className="is-age">{ageLabel}</span>
      </div>

      <Formik
        initialValues={initialValues}
        validationSchema={schema}
        validateOnMount
        onSubmit={values => {
          const payload: any = { ...values };
          if (!payload.pitchId) delete payload.pitchId;
          onSubmit(payload);
        }}
      >
        {({ values, touched, errors, setFieldValue, isValid }) => {
          const matchingTeams=teams.filter(team=>(team.groupIds||[]).map(String).includes(String(values.tournamentGroupId))&&(team.ages||[]).map(String).includes(String(values.age))&&(values.gender===GENDER.MIXED||team.gender===values.gender));
          const participantCount=isMiniTeamTournament(tournament)
            ? matchingTeams.reduce((count,team)=>count+(team.players||[]).length,0)
            : matchingTeams.length;
          const matchesPerRound=participantCount>1?participantCount*(participantCount-1)/2:0;
          const matchCount=matchesPerRound*Number(values.rounds||1);
          const totalMinutes=matchCount*Number(values.eventDuration||0)+Math.max(0,matchCount-1)*Number(values.breakBetweenEvents||0);
          const projectedFinish=new Date(+new Date(values.startTime)+totalMinutes*60000);
          const tournamentFinish=new Date(tournament.endTime);
          const exceedsTournament=matchCount>0&&Number.isFinite(projectedFinish.getTime())&&projectedFinish>tournamentFinish;
          return <Form aria-busy={isSubmitting}>
            {isSubmitting?<div className="tournamentGroupEventsForm__progress" role="status"><span aria-hidden="true"/><div><strong>Preparing group events…</strong><small>Please wait while the fixtures are checked and created.</small></div></div>:null}
            <div className="tournamentGroupEventsForm__row">
              <label>
                <span>
                  Group <i>*</i>
                </span>
                <Field
                  name="tournamentGroupId"
                  component="select"
                  onChange={(event: React.ChangeEvent<HTMLSelectElement>) => {
                    const group = groups.find(item => String(item.id) === String(event.target.value));
                    setFieldValue('tournamentGroupId', event.target.value);
                    setFieldValue('rounds', group?.roundRobinRounds || 1);
                    setFieldValue('competitionOutcome', group?.competitionOutcome || 'KNOCKOUT');
                    setFieldValue('awardPlaces', group?.awardPlaces || 3);
                  }}
                >
                  {groups.length ? (
                    groups.map(group => (
                      <option key={group.id} value={group.id}>
                        {group.name}
                      </option>
                    ))
                  ) : (
                    <option value="">No groups available</option>
                  )}
                </Field>
                {touched.tournamentGroupId && errors.tournamentGroupId ? (
                  <small>{String(errors.tournamentGroupId)}</small>
                ) : null}
              </label>

              <label>
                <span>Pitch</span>
                <PitchPicker pitches={pitches} value={values.pitchId} onChange={value=>setFieldValue('pitchId',value)}/>
              </label>
            </div>

            <div className="tournamentGroupEventsForm__format">
              <div>
                <span>Round-robin format</span>
                <div className="tournamentGroupEventsForm__formatOptions">
                  {[1, 2].map(rounds => (
                    <button
                      type="button"
                      className={Number(values.rounds) === rounds ? 'is-selected' : ''}
                      onClick={() => setFieldValue('rounds', rounds)}
                      key={rounds}
                    >
                      <strong>{rounds === 1 ? 'Single round' : 'Double round'}</strong>
                      <small>{rounds === 1 ? 'Each team plays every other team once.' : 'Each pairing is played twice with reversed order.'}</small>
                    </button>
                  ))}
                  <label className="tournamentGroupEventsForm__customRounds">
                    <span>Custom</span>
                    <Field name="rounds" type="number" min="1" max="10" step="1" />
                    <small>rounds</small>
                  </label>
                </div>
              </div>
              <label>
                <span>How is the winner decided?</span>
                <Field name="competitionOutcome" component="select">
                  <option value="KNOCKOUT">Teams progress to knockout matches</option>
                  <option value="OVERALL_STANDINGS">Final positions come from the overall table</option>
                </Field>
              </label>
              {values.competitionOutcome === 'OVERALL_STANDINGS' ? (
                <label className="tournamentGroupEventsForm__awardPlaces">
                  <span>Places to award</span>
                  <div className="tournamentGroupEventsForm__number">
                    <Field name="awardPlaces" type="number" min="1" max={Math.max(1, participantCount)} step="1" />
                    <span>places</span>
                  </div>
                  <small>The public site will present these teams as the final podium.</small>
                </label>
              ) : null}
            </div>

            <div className="tournamentGroupEventsForm__timing">
              <DatePicker
                label="Date"
                value={values.startTime}
                onChange={(date: Date | null) =>
                  date && setFieldValue('startTime', applyDatePart(values.startTime, date))
                }
                format="DD MMM YYYY"
                required
                clearable={false}
                className="tournamentGroupEventsForm__picker"
                error={touched.startTime && errors.startTime ? String(errors.startTime) : undefined}
              />
              <TimePicker
                label="First event starts"
                value={values.startTime}
                onChange={(time: Date | null) =>
                  time && setFieldValue('startTime', applyTimePart(values.startTime, time))
                }
                minuteStep={5}
                required
                showConfirm
                clearable={false}
                className="tournamentGroupEventsForm__picker"
              />
            </div>

            <div className="tournamentGroupEventsForm__durations">
              <label>
                <span>
                  Event duration <i>*</i>
                </span>
                <div className="tournamentGroupEventsForm__number">
                  <Field name="eventDuration" type="number" min="1" step="1" />
                  <span>min</span>
                </div>
                {touched.eventDuration && errors.eventDuration ? <small>{String(errors.eventDuration)}</small> : null}
              </label>
              <label>
                <span>
                  Break between events <i>*</i>
                </span>
                <div className="tournamentGroupEventsForm__number">
                  <Field name="breakBetweenEvents" type="number" min="0" step="1" />
                  <span>min</span>
                </div>
                {touched.breakBetweenEvents && errors.breakBetweenEvents ? (
                  <small>{String(errors.breakBetweenEvents)}</small>
                ) : null}
              </label>
            </div>

            <p className="tournamentGroupEventsForm__hint">
              Each next event starts after the event duration and the break between events.
            </p>

            {matchCount>0?<div className={`tournamentGroupEventsForm__schedulePreview ${exceedsTournament?'is-warning':''}`} role={exceedsTournament?'alert':undefined}>
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 3 2.6 20h18.8L12 3zm0 5.2v5.6m0 2.8v.2"/></svg>
              <div><strong>{matchCount} matches across {values.rounds} {Number(values.rounds)===1?'round':'rounds'} · expected finish {projectedFinish.toLocaleString('en-GB',{day:'numeric',month:'short',hour:'2-digit',minute:'2-digit'})}</strong>{exceedsTournament?<span>Some matches will start or finish after the tournament ends at {tournamentFinish.toLocaleString('en-GB',{day:'numeric',month:'short',hour:'2-digit',minute:'2-digit'})}. You can still create them and adjust their times afterwards.</span>:<span>The generated schedule fits within the tournament finish time.</span>}</div>
            </div>:null}

            <footer>
              <button type="button" className="sit-btn sit-btn--secondary" disabled={isSubmitting} onClick={onCancel}>
                Cancel
              </button>
              <button type="submit" className="sit-btn sit-btn--primary" disabled={!isValid||isSubmitting}>
                {isSubmitting?<><span className="tournamentGroupEventsForm__spinner" aria-hidden="true"/>Preparing…</>:'Create events'}
              </button>
            </footer>
          </Form>;
        }}
      </Formik>
    </div>
  );
};

TournamentEventsGroupForm.displayName = 'TournamentEventsGroupForm';
